import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Gift, Users, DollarSign, Share2 } from "lucide-react";
import { API } from "../constants";
import { useAuth } from "../context/AuthContext";

const ReferralsPage = () => {
  const { token, user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!token) {
      setLoading(false);
      return;
    }
    axios.get(`${API}/referrals/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load referrals'))
      .finally(() => setLoading(false));
  }, [token, authLoading]);

  const referralLink = data ? `${window.location.origin}/register?ref=${data.referral_code}` : '';

  const copy = (text, label) => {
    if (!navigator.clipboard) {
      toast.error('Clipboard not available');
      return;
    }
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied!`)).catch(() => toast.error('Copy failed'));
  };

  const shareNative = async () => {
    if (!navigator.share) {
      copy(referralLink, 'Link');
      return;
    }
    try {
      await navigator.share({
        title: 'Cut Above Polish Shop',
        text: 'Get free 1-year VIP and 30% off your first order at Cut Above Polish Shop. Use my link:',
        url: referralLink,
      });
    } catch (e) { /* user cancelled */ }
  };

  if (!authLoading && !token) {
    return (
      <div className="min-h-screen bg-[#050505] pt-24 flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="font-heading text-3xl mb-4">Login Required</h1>
          <p className="text-gray-400 mb-6">Sign in to view your referral link and store credit.</p>
          <Link to="/login" className="bg-[#D62828] hover:bg-[#B91C1C] text-white px-8 py-3 rounded-sm font-bold uppercase tracking-wider">Login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pt-24" data-testid="referrals-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="font-heading text-5xl tracking-wide mb-2">REFER A FRIEND</h1>
        <p className="text-gray-400 mb-10 max-w-2xl">Share your link. When a friend makes their first purchase, you earn ${data?.credit_per_referral?.toFixed(2) || '10.00'} in store credit applied automatically at checkout. No limit.</p>

        {loading || !data ? (
          <p className="text-gray-400">Loading...</p>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <div className="bg-[#121212] border border-white/5 rounded-sm p-5">
                <div className="flex items-center gap-2 text-gray-400 text-xs uppercase tracking-wide mb-1">
                  <DollarSign className="w-3 h-3" /> Store Credit
                </div>
                <p className="text-3xl font-heading text-green-400" data-testid="referrals-balance">${data.store_credit_balance.toFixed(2)}</p>
              </div>
              <div className="bg-[#121212] border border-white/5 rounded-sm p-5">
                <div className="flex items-center gap-2 text-gray-400 text-xs uppercase tracking-wide mb-1">
                  <Gift className="w-3 h-3" /> Total Earned
                </div>
                <p className="text-3xl font-heading text-white" data-testid="referrals-earned">${data.total_earned.toFixed(2)}</p>
              </div>
              <div className="bg-[#121212] border border-white/5 rounded-sm p-5">
                <div className="flex items-center gap-2 text-gray-400 text-xs uppercase tracking-wide mb-1">
                  <Users className="w-3 h-3" /> Friends Referred
                </div>
                <p className="text-3xl font-heading text-white" data-testid="referrals-count">{data.referees.length}</p>
              </div>
            </div>

            <div className="bg-[#121212] border border-white/10 rounded-sm p-6 mb-8">
              <p className="text-xs uppercase tracking-wide text-gray-400 mb-3">Your Referral Code</p>
              <div className="flex items-center gap-3 mb-4">
                <code className="bg-[#0F0F0F] border border-white/10 px-4 py-3 rounded-sm text-2xl font-bold tracking-[0.4em] text-[#D62828] flex-1 text-center" data-testid="referrals-code">{data.referral_code}</code>
                <button onClick={() => copy(data.referral_code, 'Code')} className="bg-white/10 hover:bg-white/20 text-white p-3 rounded-sm transition-colors" data-testid="referrals-copy-code">
                  <Copy className="w-5 h-5" />
                </button>
              </div>

              <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">Sharable Link</p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={referralLink}
                  className="flex-1 bg-[#0F0F0F] border border-white/10 text-gray-300 text-sm rounded-sm h-11 px-3"
                  data-testid="referrals-link"
                />
                <button onClick={() => copy(referralLink, 'Link')} className="bg-white/10 hover:bg-white/20 text-white px-4 py-3 rounded-sm transition-colors text-sm font-semibold" data-testid="referrals-copy-link">
                  <Copy className="w-4 h-4 inline mr-1" /> Copy
                </button>
                <button onClick={shareNative} className="bg-[#D62828] hover:bg-[#B91C1C] text-white px-4 py-3 rounded-sm transition-colors text-sm font-semibold" data-testid="referrals-share">
                  <Share2 className="w-4 h-4 inline mr-1" /> Share
                </button>
              </div>
            </div>

            <div className="bg-[#121212] border border-white/5 rounded-sm p-6">
              <h2 className="font-heading text-2xl mb-4">YOUR REFERRALS</h2>
              {data.referees.length === 0 ? (
                <p className="text-gray-500 text-sm">No referrals yet. Share your link to start earning.</p>
              ) : (
                <div className="space-y-2">
                  {data.referees.map(r => (
                    <div key={r.user_id} className="flex items-center justify-between bg-[#0F0F0F] border border-white/5 rounded-sm p-3" data-testid={`referee-${r.user_id}`}>
                      <div>
                        <p className="text-white text-sm font-semibold">{r.name}</p>
                        <p className="text-gray-500 text-xs">{r.email_masked} • Joined {new Date(r.joined_at).toLocaleDateString()}</p>
                      </div>
                      {r.earned_credit ? (
                        <span className="text-green-400 text-xs font-semibold uppercase tracking-wide bg-green-500/10 px-3 py-1 rounded-sm">+${data.credit_per_referral} earned</span>
                      ) : (
                        <span className="text-gray-500 text-xs uppercase tracking-wide bg-white/5 px-3 py-1 rounded-sm">Awaiting first purchase</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReferralsPage;
